import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, TimeoutError } from 'rxjs';
import { catchError, filter, take, switchMap, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ApiAuthService } from '../services/api-auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private isLoggingOut = false;
  private apiAuthService = inject(ApiAuthService);
  private router = inject(Router);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isUpload =
      req.body instanceof FormData ||
      req.url.includes('/storage/upload');
    // Uploads need longer timeout on mobile (camera photos over Capacitor HTTP).
    const timeoutMs = isUpload ? 300000 : req.url.includes('/auth/') ? 45000 : 30000;

    // Skip token for auth endpoints and public endpoints
    if (this.shouldSkipToken(req)) {
      return next.handle(req).pipe(
        timeout(timeoutMs),
        catchError((error: HttpErrorResponse | TimeoutError) => {
          if (error instanceof TimeoutError) {
            return throwError(() => new HttpErrorResponse({
              error: 'Request timeout',
              status: 408,
              statusText: 'Request Timeout',
              url: req.url,
            }));
          }
          return throwError(() => error);
        }),
      );
    }

    // Add access token to request
    const accessToken = this.apiAuthService.getToken();
    if (accessToken) {
      req = this.addToken(req, accessToken);
    }

    return next.handle(req).pipe(
      timeout(timeoutMs),
      catchError((error: HttpErrorResponse | TimeoutError) => {
        // Handle timeout errors
        if (error instanceof TimeoutError) {
          const timeoutError = new HttpErrorResponse({
            error: 'Request timeout',
            status: 408,
            statusText: 'Request Timeout',
            url: req.url
          });
          return throwError(() => timeoutError);
        }

        // Earnings internal password check returns legacy 401 on some stacks; never treat as session loss.
        if (error.status === 401 && this.isVendorEarningsVerifyAccessUrl(req.url)) {
          return throwError(() => error);
        }

        // If 401 and we have a refresh token, try to refresh
        if (error.status === 401 && this.apiAuthService.getRefreshToken()) {
          return this.handle401Error(req, next);
        }

        // If refresh fails or no refresh token, logout
        if (error.status === 401 && !this.isLoggingOut) {
          this.isLoggingOut = true;
          this.apiAuthService.clearTokens();
          this.redirectToLogin();
        }

        return throwError(() => error);
      })
    );
  }

  private isVendorEarningsVerifyAccessUrl(url: string): boolean {
    return url.includes('vendor-earnings/verify-access') || url.includes('vendor-earnings%2Fverify-access');
  }

  private shouldSkipToken(req: HttpRequest<any>): boolean {
    // Skip token for auth endpoints
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh'];
    return authEndpoints.some(endpoint => req.url.includes(endpoint));
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.apiAuthService.refreshToken().pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          const newAccessToken = response.access_token;
          this.refreshTokenSubject.next(newAccessToken);
          return next.handle(this.addToken(req, newAccessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          // Only force logout on real auth failure (invalid refresh token).
          // For transient issues (network/timeouts/server), keep tokens and let the UI show the error.
          const status = err?.status ?? 0;
          if (status === 401 && !this.isLoggingOut) {
            this.isLoggingOut = true;
            this.apiAuthService.clearTokens();
            this.redirectToLogin();
          }
          return throwError(() => err);
        })
      );
    } else {
      // Wait for refresh to complete
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token!)))
      );
    }
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private redirectToLogin(): void {
    // Only redirect if not already on login page
    const currentUrl = this.router.url;
    if (currentUrl.includes('/login') || currentUrl.includes('/auth/login')) {
      this.isLoggingOut = false;
      return;
    }

    // Try /login first (user app), fallback to /auth/login (vendor/admin/delivery apps)
    this.router
      .navigate(['/login'])
      .catch(() => this.router.navigate(['/auth/login']))
      .finally(() => {
        this.isLoggingOut = false;
      });
  }
}

